<template>
  <!-- Table Container Card -->
  <b-card no-body>
    <div
      v-if="corporateAccountSuccessCount.length || corporateAccountFailed.length"
      class="m-2"
    >
      <b-row>
        <b-col
          v-if="corporateAccountSuccessCount"
          md="6"
          class="mb-2"
        >
          <b-alert
            show
            variant="primary"
            class="p-2"
          >
            {{ corporateAccountSuccessCount }}
          </b-alert>
        </b-col>
        <b-col
          v-if="corporateAccountFailed.length"
          md="6"
          class="mb-2"
        >
          <div>
            <b-button variant="danger">
              {{ $t('FAILED LISTS') }}
            </b-button>
          </div>
          <b-table
            ref="corporateAccountFailedReport"
            :items="corporateAccountFailed"
            responsive
            :fields="failedHeaders"
            primary-key="id"
            show-empty
            :empty-text="$t('No records available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          /></b-col>
      </b-row>
    </div>

    <div class="m-2">
      <b-row class="d-flex align-items-center">
        <b-col md="6">
          <validation-observer ref="corporateCustomerAccountRules">
            <b-form>
              <b-col
                md="12"
                class="mb-2"
              >
                <div class="mb-1">
                  {{ $t('Select file (.xlsx format only)') }}
                </div>
                <b-form-file
                  id="file-name"
                  v-model="hasFileData"
                  :label="$t('Select file (.xlsx format only)')"
                  :state="Boolean(hasFileData)"
                  :placeholder="$t('Choose a file or drop it here...')"
                  :drop-placeholder="$t('Drop file here...')"
                  error-name="file"
                  rules="required"
                  accept=".xlsx"
                  @change="onChangeXLSX($event)"
                />
              </b-col>
            </b-form>
          </validation-observer>
        </b-col>

        <b-col
          md="6"
        >
          <b-row>
            <b-col md="5">
              <Button
                v-if="getUserPermission('DATA_MIGRATION') && parseExcelFile.length"
                :label="$t('Next >')"
                size="md"
                :hidden="isNextValidateHidden"
                :loading="isLoading"
                @click="handleNextValidate"
              />
              <Button
                v-if="getUserPermission('DATA_MIGRATION') && !errorMessages.length"
                :label="$t('Create Corporate Customer Accounts')"
                size="md"
                :hidden="isCreateBatchHidden"
                :loading="isLoading"
                :disabled="isDisabled"
                @click="handleCreate"
              />
            </b-col>
            <b-col md="7">
              <Button
                class="ml-lg-2"
                align="end"
                :label="$t('Download Corporate Customer Account Format')"
                size="md"
                icon="DownloadIcon"
                show-icon
                :loading="isLoading2"
                :disabled="isDisabled2"
                @click="handleDownloadSubmit"
              />
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </div>
    <b-table
      v-if="errorMessages.length"
      ref="corporateAccountSuccessCountReport"
      :items="errorMessages"
      responsive
      :fields="translatedErrorHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    />
    <p
      v-if="totalSheetRows"
      class="p-2"
      style="font-size: 18px;"
    >
      {{ $t('There are total of ') }} ({{ totalSheetRows }}) {{ $t('Records fetched') }}
    </p>
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BForm, BTable, BFormFile, BButton, BAlert,
} from 'bootstrap-vue'
import { required } from '@validations'
import { ValidationObserver } from 'vee-validate'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
// import Papa from 'papaparse'
import XLSX from 'xlsx'
import Button from '../../../components/button/Button.vue'
import { batchAccountFormat, tableLessHeaders, tableAllHeaders } from '../../../../helpers/data'
import { toYearWithDate } from '../../../../helpers/formatDate'

export default {
  components: {
    BCard,
    BTable,
    BRow,
    BCol,
    BForm,
    BFormFile,
    ValidationObserver,
    Button,
    BButton,
    BAlert,
  },
  filters: {
    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      totalSheetRows: '',
      fileData: [],
      hasFileData: [],
      parseExcelFile: [],
      validatedExcelFileData: [],
      sortOrders: {},
      accountFormat: batchAccountFormat,
      successHeaders: tableAllHeaders,
      failedHeaders: tableLessHeaders,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'oldCorporateCustomerID', sortable: true, label: 'Old Corporate Customer ID' },
        { key: 'customerType', sortable: true },
        { key: 'corporateName', sortable: true, label: 'Corporate\'s Name' },
        { key: 'description', sortable: true },
        { key: 'email', sortable: true, label: 'Corporate\'s Email' },
        { key: 'businessPhoneNumber', sortable: true, label: 'Corporate\'s Phone Number' },
        { key: 'address', sortable: true, label: 'Corporate\'s Address' },
        { key: 'countryOfOperation', sortable: true, label: 'Country of Operation' },
        { key: 'stateOfOperation', sortable: true, label: 'State of Operation' },
        { key: 'localGovernmentOfOperation', sortable: true, label: 'Local Govt of Operation' },
        { key: 'contactPersonName', sortable: true, label: 'Corporate Contact Person\'s Name' },
        { key: 'contactPersonPhoneNumber', sortable: true, label: 'Corporate Contact Person\'s Phone No' },
        { key: 'natureOfBusiness', sortable: true, label: 'Nature of Business' },
        { key: 'industrialSector', sortable: true, label: 'Corporate\'s Industrial Sector' },
        { key: 'incorporationDate', sortable: true, label: 'Corporate\'s Incorporation Date' },
        { key: 'registrationNumber', sortable: true, label: 'Corporate\'s Registration Number' },
        { key: 'tin', sortable: true, label: 'Tax Identification Number' },
        { key: 'signatory' },
        { key: 'mandateRule' },
        { key: 'referenceAccountNumber', sortable: true },
        { key: 'referenceNuban', sortable: true },
        { key: 'bvn', sortable: true, label: 'Bank Verification Number' },
        { key: 'accountName', sortable: true, label: 'Corporate Account Name' },
        { key: 'accountType', sortable: true },
        { key: 'accountStatus', sortable: true },
        { key: 'branchCode', sortable: true },
        { key: 'productCode', sortable: true },
        { key: 'accountOfficerStaffID', sortable: true },
        { key: 'accountTierLevel', sortable: true },
        { key: 'accessLevel', sortable: true },
        { key: 'enableEmailNotification', sortable: true },
        { key: 'enableSMSNotification', sortable: true },
        { key: 'statementDeliveryMode', sortable: true },
        { key: 'statementDeliveryFrequency', sortable: true },
        { key: 'minimumBalanceRequired', sortable: true },
        { key: 'referee1CustomerID', sortable: true },
        { key: 'referee2CustomerID', sortable: true },
      ],
      required,
      isDisabled: false,
      isLoading: false,
      isDisabled2: false,
      isLoading2: false,
      showFormError: false,
      isCreateBatchHidden: true,
      isNextValidateHidden: false,
      corporateAccountSuccessCount: [],
      corporateAccountFailed: [],
      sortDesc: false,
      sortBy: 'corporateName',
      errorHeaders: [
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'message', sortable: true, label: 'Error Messages' },
      ],
      errorMessages: [],
    }
  },
  computed: {
    translatedErrorHeaders() {
      return this.errorHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      corporateAccountCreation: 'batch/corporateAccountCreation',
      downloadAccountFormat: 'batch/downloadAccountFormat',
      validateCorporateAccountCreation: 'batch/validateCorporateAccountCreation',
    }),
    async handleCreate() {
      this.$refs.corporateCustomerAccountRules.validate().then(success => {
        if (success) {
          this.confirm(this.corporateAccountCreationAction, { text: this.$t('Are you sure you want to Create Corporate Customer Accounts?') })
        }
      })
    },
    async corporateAccountCreationAction() {
      try {
        this.corporateAccountSuccessCount = ''
        this.showFormError = false
        this.isDisabled = true
        this.isLoading = true
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          customerType: item.customerType ? Number(item.customerType) : null,
          email: item.email ? item.email : null,
          businessPhoneNumber: item.businessPhoneNumber ? item.businessPhoneNumber : null,
          registrationNumber: item.registrationNumber ? item.registrationNumber : null,
          incorporationDate: item.incorporationDate ? toYearWithDate(item.incorporationDate) : null,
          accountType: item.accountType ? Number(item.accountType) : null,
          industrialSector: item.industrialSector ? Number(item.industrialSector) : null,
          accountStatus: item.accountStatus ? Number(item.accountStatus) : null,
          accountTierLevel: item.accountTierLevel ? Number(item.accountTierLevel) : 1,
          accessLevel: item.accessLevel ? Number(item.accessLevel) : 1,
          enableEmailNotification: item.enableEmailNotification ? Boolean(item.enableEmailNotification) : false,
          enableSMSNotification: item.enableSMSNotification ? Boolean(item.enableSMSNotification) : false,
          statementDeliveryMode: item.statementDeliveryMode ? Number(item.statementDeliveryMode) : null,
          statementDeliveryFrequency: item.statementDeliveryFrequency ? Number(item.statementDeliveryFrequency) : null,
          minimumBalanceRequired: item.minimumBalanceRequired ? Number(item.minimumBalanceRequired) : 0,
        }))
        const response = await this.corporateAccountCreation({ payload: formData })
        const { status, data } = response
        if (status === 200) {
          this.hasFileData = []
          this.parseExcelFile = []
          if (data.successCount > 0) {
            this.corporateAccountSuccessCount = `(${data.successCount}) Corporate Customer Accounts was successfully created`
          } else {
            this.corporateAccountSuccessCount = `(${data.successCount}) Corporate Customer Account was successfully created`
          }
          this.corporateAccountFailed = data.failed
          this.isLoading = false
          this.isDisabled = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          this.alertError({ text: `Your request to create Corporate Customer Accounts failed. Reason: ${responseError.errorMessage}` })
        })
        this.showFormError = true
      } finally {
        this.isDisabled = false
        this.isLoading = false
      }
    },
    onChangeXLSX(event) {
      this.errorMessages = []
      this.isCreateBatchHidden = true
      this.fileData = event.target.files ? event.target.files[0] : null
      if (this.fileData) {
        const vm = this
        const reader = new FileReader()
        reader.onload = e => {
          const data = e.target.result
          const workbook = XLSX.read(data, { type: 'binary', cellDates: true, dateNF: 'mm/dd/yyyy;@' })
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const rowObject = XLSX.utils.sheet_to_row_object_array(worksheet, { raw: false })
          vm.totalSheetRows = rowObject.length
          vm.parseExcelFile = JSON.parse(JSON.stringify(rowObject)).map(items => ({
            oldCorporateCustomerID: items['Old Corporate Customer ID'],
            customerType: items['Customer Type'],
            corporateName: items['Corporate Name'],
            description: items.Description,
            businessPhoneNumber: items['Corporate\'s Phone Number'],
            email: items['Corporate\'s Email'],
            address: items['Corporate\'s Address'],
            countryOfOperation: items['Country of Operation'],
            stateOfOperation: items['State of Operation'],
            localGovernmentOfOperation: items['Local Govt of Operation'],
            contactPersonName: items['Corporate Contact Person\'s Name'],
            contactPersonPhoneNumber: items['Corporate\'s Contact Person Phone No'],
            natureOfBusiness: items['Nature of Business'],
            industrialSector: items['Corporate\'s Industrial Sector'],
            incorporationDate: items['Corporate\'s Incorporation Date'],
            registrationNumber: items['Corporate\'s RC Number'],
            tin: items['Tax Identification Number'],
            signatory: items['Signatory '],
            mandateRule: items['Mandate Rule'],
            referenceAccountNumber: items['Reference Account Number'],
            referenceNuban: items['Reference NUBAN'],
            bvn: items['Bank Verification Number'],
            accountName: items['Corporate Account Name'],
            accountType: items['Account Type'],
            accountStatus: items['Account Status'],
            branchCode: items['Branch Code'],
            productCode: items['Product Code'],
            accountOfficerStaffID: items['Account Officer Staff ID'],
            accountTierLevel: items['Account Tier Level'],
            accessLevel: items['Access Level'],
            enableEmailNotification: items['Enable Email Notification'],
            enableSMSNotification: items['Enable SMS Notification'],
            statementDeliveryMode: items['Statement Delivery Mode'],
            statementDeliveryFrequency: items['Statement Delivery Frequency'],
            minimumBalanceRequired: items['Minimum Balance Required'],
            referee1CustomerID: items['Referee1 Customer ID'],
            referee2CustomerID: items['Referee2 Customer ID'],
          }))
        }
        reader.readAsBinaryString(this.fileData)
      }
    },
    async handleNextValidate() {
      try {
        this.isLoading = true
        this.hasFileData = []
        const formData = this.parseExcelFile.map(item => ({
          ...item,
          customerType: item.customerType ? Number(item.customerType) : null,
          email: item.email ? item.email : null,
          businessPhoneNumber: item.businessPhoneNumber ? item.businessPhoneNumber : null,
          registrationNumber: item.registrationNumber ? item.registrationNumber : null,
          incorporationDate: item.incorporationDate ? toYearWithDate(item.incorporationDate) : null,
          accountType: item.accountType ? Number(item.accountType) : null,
          industrialSector: item.industrialSector ? Number(item.industrialSector) : null,
          accountStatus: item.accountStatus ? Number(item.accountStatus) : null,
          accountTierLevel: item.accountTierLevel ? Number(item.accountTierLevel) : 1,
          accessLevel: item.accessLevel ? Number(item.accessLevel) : 1,
          enableEmailNotification: item.enableEmailNotification ? Boolean(item.enableEmailNotification) : false,
          enableSMSNotification: item.enableSMSNotification ? Boolean(item.enableSMSNotification) : false,
          statementDeliveryMode: item.statementDeliveryMode ? Number(item.statementDeliveryMode) : null,
          statementDeliveryFrequency: item.statementDeliveryFrequency ? Number(item.statementDeliveryFrequency) : null,
          minimumBalanceRequired: item.minimumBalanceRequired ? Number(item.minimumBalanceRequired) : 0,
        }))
        const response = await this.validateCorporateAccountCreation({ payload: formData })
        const { status, data } = response
        if (status === 200) {
          this.errorMessages = data.failed
          if (data.successCount > 1) {
            const accountsText = this.$t('Corporate Customer Accounts are ready to be created')
            this.corporateAccountSuccessCount = `(${data.successCount}) ${accountsText}`
          } else {
            const accText = this.$t('Corporate Customer Account is ready to be created')
            this.corporateAccountSuccessCount = `(${data.successCount}) ${accText}`
          }
          this.isCreateBatchHidden = false
          if (this.errorMessages.length) {
            this.isNextValidateHidden = false
          } else {
            this.isNextValidateHidden = true
            this.isDisabled = false
            this.alertSuccess({ text: this.$t('Everything seems good! Continue with Corporate Customer Account Creation') })
          }
          this.isLoading = false
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const performText = this.$t('Your request to perform Corporate Customer Account validation failed. Reason')
          this.alertError({
            text: `${performText}: ${responseError.errorMessage}`,
          })
        })
        this.isCreateBatchHidden = true
        this.isLoading = false
      }
    },
    async handleDownloadSubmit() {
      this.confirm(this.downloadAction, { text: this.$t('Are you sure you want to download Corporate Customer Account Creation Format?') })
    },
    async downloadAction() {
      try {
        this.isLoading2 = true
        this.isDisabled2 = true
        const CORPORATE_CUSTOMER_ACCOUNT_CREATION_TEMPLATE = 12
        const response = await this.downloadAccountFormat(CORPORATE_CUSTOMER_ACCOUNT_CREATION_TEMPLATE)
        if (response.status === 200) {
          const downloadAnchor = document.createElement('a')
          downloadAnchor.setAttribute('href', response.data.data)
          const filename = 'Udara360-Corporate-Customer-Account-Creation-Data-Migration.xlsx'
          downloadAnchor.setAttribute('download', filename)
          document.body.appendChild(downloadAnchor)
          downloadAnchor.click()
          document.body.removeChild(downloadAnchor)
          this.alertSuccess({ text: this.$t('Your request to download Corporate Customer Account Creation Format was successful') })
        }
      } catch (error) {
        error.response.data.errors.forEach(responseError => {
          const reasonText = this.$t('Your request to download Corporate Customer Account Creation Format failed. Reason')
          this.alertError({ text: `${reasonText}: ${responseError.errorMessage}` })
          this.isLoading2 = false
          this.isDisabled2 = false
        })
      } finally {
        this.isLoading2 = false
        this.isDisabled2 = false
      }
    },
  },
}
</script>
